import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type {
  SurveyStateDto,
  SurveyTypeDto,
  SurveyTypeListItemDto,
  SurveyVersionDto,
  SurveyVersionListItemDto,
  SurveyQuestionDto,
  SurveyAnswerDto,
  PromptFragmentDto,
  FragmentTriggerRowDto,
  CallToActionThresholdRowDto,
  CreateSurveyTypeRequest,
  UpdateSurveyTypeRequest,
  SetArchivedRequest,
  CloneSurveyTypeRequest,
  CreateSurveyVersionRequest,
  UpdateSurveyVersionRequest,
  SetVersionStateRequest,
  CopyToDraftRequest,
  EditLockStatusDto,
  EditLockAcquireResponseDto,
  AcquireEditLockRequest,
  CreateSurveyQuestionRequest,
  UpdateSurveyQuestionRequest,
  CreateSurveyAnswerRequest,
  UpdateSurveyAnswerRequest,
  CreatePromptFragmentRequest,
  UpdatePromptFragmentRequest,
  AddFragmentTriggerRequest,
  CreateCallToActionThresholdRequest,
  UpdateCallToActionThresholdRequest,
  UserVoiceDto,
  SubmitUserVoiceRequestDto
} from '../models/admin-dto';

const BASE = `${environment.apiUrl}/api/admin`;

@Injectable({ providedIn: 'root' })
export class AdminService {
  constructor(private readonly http: HttpClient) {}

  private err(err: { error?: { error?: string }; message?: string }): Observable<never> {
    const msg = err?.error?.error ?? err?.message ?? 'An error occurred.';
    return throwError(() => new Error(msg));
  }

  getSurveyStates(): Observable<SurveyStateDto[]> {
    return this.http.get<SurveyStateDto[]>(`${BASE}/lookups/survey-states`).pipe(catchError((e) => this.err(e)));
  }

  getSurveyTypes(): Observable<SurveyTypeListItemDto[]> {
    return this.http.get<SurveyTypeListItemDto[]>(`${BASE}/survey-types`).pipe(catchError((e) => this.err(e)));
  }

  getSurveyType(id: number): Observable<SurveyTypeDto> {
    return this.http.get<SurveyTypeDto>(`${BASE}/survey-types/${id}`).pipe(catchError((e) => this.err(e)));
  }

  createSurveyType(req: CreateSurveyTypeRequest): Observable<SurveyTypeDto> {
    return this.http.post<SurveyTypeDto>(`${BASE}/survey-types`, req).pipe(catchError((e) => this.err(e)));
  }

  updateSurveyType(id: number, req: UpdateSurveyTypeRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/survey-types/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  deleteSurveyType(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/survey-types/${id}`).pipe(catchError((e) => this.err(e)));
  }

  setSurveyTypeArchived(id: number, req: SetArchivedRequest): Observable<void> {
    return this.http.patch<void>(`${BASE}/survey-types/${id}/archived`, req).pipe(catchError((e) => this.err(e)));
  }

  cloneSurveyType(sourceId: number, req: CloneSurveyTypeRequest): Observable<{ surveyTypeId: number; surveyVersionId: number }> {
    return this.http
      .post<{ surveyTypeId: number; surveyVersionId: number }>(`${BASE}/survey-types/${sourceId}/clone`, req)
      .pipe(catchError((e) => this.err(e)));
  }

  getSurveyVersionsByType(surveyTypeId: number): Observable<SurveyVersionListItemDto[]> {
    return this.http
      .get<SurveyVersionListItemDto[]>(`${BASE}/survey-versions/by-type/${surveyTypeId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  getSurveyVersion(id: number): Observable<SurveyVersionDto> {
    return this.http.get<SurveyVersionDto>(`${BASE}/survey-versions/${id}`).pipe(catchError((e) => this.err(e)));
  }

  createSurveyVersion(req: CreateSurveyVersionRequest): Observable<SurveyVersionDto> {
    return this.http.post<{ id: number }>(`${BASE}/survey-versions`, req).pipe(
      switchMap((res) => this.getSurveyVersion(res.id)),
      catchError((e) => this.err(e))
    );
  }

  updateSurveyVersion(id: number, req: UpdateSurveyVersionRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/survey-versions/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  setSurveyVersionState(id: number, req: SetVersionStateRequest): Observable<void> {
    return this.http.patch<void>(`${BASE}/survey-versions/${id}/state`, req).pipe(catchError((e) => this.err(e)));
  }

  /** Mark the survey version as previewed so Publish can be enabled (preview-before-publish requirement). */
  markSurveyVersionPreviewed(id: number): Observable<void> {
    return this.http.patch<void>(`${BASE}/survey-versions/${id}/mark-previewed`, {}).pipe(catchError((e) => this.err(e)));
  }

  deleteSurveyVersion(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/survey-versions/${id}`).pipe(catchError((e) => this.err(e)));
  }

  copySurveyVersionToDraft(id: number, req: CopyToDraftRequest): Observable<SurveyVersionDto> {
    return this.http.post<SurveyVersionDto>(`${BASE}/survey-versions/${id}/copy-to-draft`, req).pipe(catchError((e) => this.err(e)));
  }

  /** Edit lock: get current lock status (for showing "Other admin is currently editing"). */
  getEditLockStatus(versionId: number): Observable<EditLockStatusDto> {
    return this.http
      .get<EditLockStatusDto>(`${BASE}/survey-versions/${versionId}/edit-lock`)
      .pipe(catchError((e) => this.err(e)));
  }

  /**
   * Edit lock: acquire lock for current user. On 409 Conflict (another admin has lock),
   * returns { success: false, message } instead of throwing so the UI can show read-only + banner.
   */
  acquireEditLock(versionId: number, body?: AcquireEditLockRequest): Observable<EditLockAcquireResponseDto> {
    return this.http
      .post<EditLockAcquireResponseDto>(`${BASE}/survey-versions/${versionId}/edit-lock`, body ?? {}, { observe: 'response' })
      .pipe(
        map((r) => r.body ?? { success: false, message: null }),
        catchError((e: unknown) => {
          const err = e as { status?: number; error?: EditLockAcquireResponseDto };
          if (err?.status === 409 && err?.error) return of(err.error);
          return this.err(e as { error?: { error?: string }; message?: string });
        })
      );
  }

  /** Edit lock: release lock when saving or navigating away. */
  releaseEditLock(versionId: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/survey-versions/${versionId}/edit-lock`).pipe(catchError((e) => this.err(e)));
  }

  /** Edit lock: extend expiry (e.g. every 10 minutes while edit page is open). On 409, lock was lost or expired. */
  refreshEditLock(versionId: number, body?: AcquireEditLockRequest): Observable<{ success: boolean }> {
    return this.http
      .post<{ success: boolean }>(`${BASE}/survey-versions/${versionId}/edit-lock/refresh`, body ?? {}, { observe: 'response' })
      .pipe(
        map((r) => r.body ?? { success: false }),
        catchError((e: unknown) => {
          const err = e as { status?: number };
          if (err?.status === 409) return of({ success: false });
          return this.err(e as { error?: { error?: string }; message?: string });
        })
      );
  }

  getSurveyQuestionsByVersion(surveyVersionId: number): Observable<SurveyQuestionDto[]> {
    return this.http
      .get<SurveyQuestionDto[]>(`${BASE}/survey-questions/by-version/${surveyVersionId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  createSurveyQuestion(req: CreateSurveyQuestionRequest): Observable<SurveyQuestionDto> {
    return this.http.post<SurveyQuestionDto>(`${BASE}/survey-questions`, req).pipe(catchError((e) => this.err(e)));
  }

  updateSurveyQuestion(id: number, req: UpdateSurveyQuestionRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/survey-questions/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  deleteSurveyQuestion(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/survey-questions/${id}`).pipe(catchError((e) => this.err(e)));
  }

  getSurveyAnswersByQuestion(surveyQuestionId: number): Observable<SurveyAnswerDto[]> {
    return this.http
      .get<SurveyAnswerDto[]>(`${BASE}/survey-answers/by-question/${surveyQuestionId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  createSurveyAnswer(req: CreateSurveyAnswerRequest): Observable<SurveyAnswerDto> {
    return this.http.post<SurveyAnswerDto>(`${BASE}/survey-answers`, req).pipe(catchError((e) => this.err(e)));
  }

  updateSurveyAnswer(id: number, req: UpdateSurveyAnswerRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/survey-answers/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  deleteSurveyAnswer(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/survey-answers/${id}`).pipe(catchError((e) => this.err(e)));
  }

  getPromptFragmentsByVersion(surveyVersionId: number): Observable<PromptFragmentDto[]> {
    return this.http
      .get<PromptFragmentDto[]>(`${BASE}/prompt-fragments/by-version/${surveyVersionId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  getFragmentTriggers(fragmentId: number): Observable<FragmentTriggerRowDto[]> {
    return this.http
      .get<FragmentTriggerRowDto[]>(`${BASE}/prompt-fragments/${fragmentId}/triggers`)
      .pipe(catchError((e) => this.err(e)));
  }

  createPromptFragment(req: CreatePromptFragmentRequest): Observable<PromptFragmentDto> {
    return this.http.post<PromptFragmentDto>(`${BASE}/prompt-fragments`, req).pipe(catchError((e) => this.err(e)));
  }

  updatePromptFragment(id: number, req: UpdatePromptFragmentRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/prompt-fragments/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  deletePromptFragment(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/prompt-fragments/${id}`).pipe(catchError((e) => this.err(e)));
  }

  addFragmentTrigger(fragmentId: number, req: AddFragmentTriggerRequest): Observable<void> {
    return this.http
      .post<void>(`${BASE}/prompt-fragments/${fragmentId}/triggers`, req)
      .pipe(catchError((e) => this.err(e)));
  }

  removeFragmentTrigger(fragmentId: number, surveyAnswerId: number): Observable<void> {
    return this.http
      .delete<void>(`${BASE}/prompt-fragments/${fragmentId}/triggers/${surveyAnswerId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  getCallToActionThresholdsByVersion(surveyVersionId: number): Observable<CallToActionThresholdRowDto[]> {
    return this.http
      .get<CallToActionThresholdRowDto[]>(`${BASE}/call-to-action-thresholds/by-version/${surveyVersionId}`)
      .pipe(catchError((e) => this.err(e)));
  }

  createCallToActionThreshold(req: CreateCallToActionThresholdRequest): Observable<CallToActionThresholdRowDto> {
    return this.http
      .post<CallToActionThresholdRowDto>(`${BASE}/call-to-action-thresholds`, req)
      .pipe(catchError((e) => this.err(e)));
  }

  updateCallToActionThreshold(id: number, req: UpdateCallToActionThresholdRequest): Observable<void> {
    return this.http.put<void>(`${BASE}/call-to-action-thresholds/${id}`, req).pipe(catchError((e) => this.err(e)));
  }

  deleteCallToActionThreshold(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/call-to-action-thresholds/${id}`).pipe(catchError((e) => this.err(e)));
  }

  getUserVoiceList(status?: string): Observable<UserVoiceDto[]> {
    const params = status != null ? { status } : {};
    return this.http.get<UserVoiceDto[]>(`${BASE}/user-voice`, { params }).pipe(catchError((e) => this.err(e)));
  }

  submitUserVoice(req: SubmitUserVoiceRequestDto): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${BASE}/user-voice`, req).pipe(catchError((e) => this.err(e)));
  }

  promptRewrite(req: { prompt: string }): Observable<{ recommendedPrompt: string }> {
    return this.http.post<{ recommendedPrompt: string }>(`${BASE}/prompt-rewrite`, req).pipe(catchError((e) => this.err(e)));
  }
}
